import React, {JSX, useEffect, useState} from "react";
import {
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Checkbox,
    IconButton,
    Grid2,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Divider,
    Typography, Collapse
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { supabase } from '../components/supabaseClient.tsx';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import Alert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import AlertTitle from "@mui/material/AlertTitle";
import PushPinIcon from "@mui/icons-material/PushPin";
import UndoIcon from "@mui/icons-material/Undo";

interface ShoppingList {
    id: number;
    created_at: string;
    Title: string;
    Description: string;
    Parts: string[];
    Store: string;
    User: string;
    Amount: string[];
    State: boolean[];
    is_pinned: boolean;
}

export default function ShoppingList() {
    const [checked, setChecked] = useState<number[]>([]);
    const [shoppingLists, setShoppingLists] = useState<ShoppingList[]>([]);
    const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);
    const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [newTitle, setNewTitle] = useState("");
    const [newDescription, setNewDescription] = useState("");
    const [newStore, setNewStore] = useState("");
    const [newItem, setNewItem] = useState("");
    const [newItems, setNewItems] = useState<string[]>([]);
    const [selectedList, setSelectedList] = useState<ShoppingList | null>(null);
    const [editTitle, setEditTitle] = useState("");
    const [editDescription, setEditDescription] = useState("");
    const [editStore, setEditStore] = useState("");
    const [editItems, setEditItems] = useState<string[]>([]);
    const [editNewItem, setEditNewItem] = useState("");
    const [newAmount, setNewAmount] = useState<string[]>([]);
    const [newItemAmount, setNewItemAmount] = useState<string>();
    const [editAmount, setEditAmount] = useState<string[]>([]);
    const [alert, setAlert] = useState<JSX.Element | null>(null);
    const [open, setOpen] = React.useState(true);

    const handleEditShoppingList = () => {
        if (checked.length === 1) {
            const listToEdit = shoppingLists.find(list => list.id === checked[0]);
            if (listToEdit) {
                setEditTitle(listToEdit.Title);
                setEditDescription(listToEdit.Description);
                setEditStore(listToEdit.Store);
                setEditItems(listToEdit.Parts);
                setEditAmount(listToEdit.Amount);
                setSelectedList(listToEdit);
                setIsEditDialogOpen(true);
            }
        }
    };

    useEffect(() => {
        const fetchShoppingLists = async () => {
            const { data, error } = await supabase
                .from('Shoppinglist')
                .select('*');
            if (error) {
                console.error(error);
            } else {
                setShoppingLists(data);
            }
        };

        fetchShoppingLists();
    }, []);

    const handleToggle = (value: number) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    };

    const handleNewShoppingList = () => {
        setIsNewDialogOpen(true);
    };

    const handleCloseNewDialog = () => {
        setIsNewDialogOpen(false);
        setNewTitle("");
        setNewDescription("");
        setNewStore("");
        setNewItem("");
        setNewItems([]);
        setNewAmount([]);
    };

    const handleToggleItemState = (index: number) => {
        if (selectedList) {
            const newState = [...selectedList.State];
            newState[index] = !newState[index];
            setSelectedList({ ...selectedList, State: newState });
        }
    };

    const handleSaveShoppingList = async () => {
        const initialState = new Array(newItems.length).fill(false);
        const { error } = await supabase
            .from('Shoppinglist')
            .insert([{ Title: newTitle, Description: newDescription, Store: newStore, Parts: newItems, Amount: newAmount, State: initialState }]);

        if (error) {
            setAlert(
                <Collapse in={open} style={{ position: 'absolute', top: '50px', right: '20px', zIndex: 1000 }}>
                    <Alert
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    setOpen(false);
                                    setAlert(null);
                                }}
                            >
                                <CloseIcon fontSize="inherit" />
                            </IconButton>
                        }
                        sx={{ mb: 2 }}
                        severity={"error"}
                    >
                        <AlertTitle>Error</AlertTitle>
                        Something went wrong!
                    </Alert>
                </Collapse>
            );
        } else {
            const { data } = await supabase
                .from('Shoppinglist')
                .select('*');
            setShoppingLists(data);
            handleCloseNewDialog();

            setAlert(
                <Collapse in={open} style={{ position: 'absolute', top: '50px', right: '20px', zIndex: 1000 }}>
                    <Alert
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    setOpen(false);
                                    setAlert(null);
                                }}
                            >
                                <CloseIcon fontSize="inherit" />
                            </IconButton>
                        }
                        sx={{ mb: 2 }}
                        severity={"success"}
                    >
                        <AlertTitle>Success</AlertTitle>
                        Shopping List saved successfully!
                    </Alert>
                </Collapse>
            );
        }
    };

    const handleAddNewItem = () => {
        if (newItem.trim() !== "") {
            setNewItems([...newItems, newItem]);
            setNewAmount([...newAmount, newItemAmount]);
            setNewItem("");
            setNewItemAmount("");
        }
    };

    const handleRemoveNewItem = (index: number) => {
        setNewItems(newItems.filter((_, i) => i !== index));
        setNewAmount(newAmount.filter((_, i) => i !== index));
    };

    const handleDeleteShoppingLists = async () => {
        if (checked.length > 0) {
            try {
                const { error } = await supabase
                    .from('Shoppinglist')
                    .delete()
                    .in('id', checked);

                if (error) {
                    throw error;
                }

                setShoppingLists(shoppingLists.filter(list => !checked.includes(list.id)));
                setChecked([]);
            } catch (error) {
                console.error('Error deleting shopping lists:', error);
            }
        }
    };

    const handleListItemClick = (list: ShoppingList) => {
        setSelectedList(list);
        setIsViewDialogOpen(true);
    };

    const handleCloseViewDialog = async () => {
        if (selectedList) {
            const { error } = await supabase
                .from('Shoppinglist')
                .update({ State: selectedList.State })
                .eq('id', selectedList.id);

            if (error) {
                console.error('Error updating item state:', error);
            }
        }
        setIsViewDialogOpen(false);
        setSelectedList(null);
    };


    const handleCloseEditDialog = () => {
        setIsEditDialogOpen(false);
        setEditTitle("");
        setEditDescription("");
        setEditStore("");
        setEditItems([]);
        setEditNewItem("");
        setSelectedList(null);
    };

    const handleSaveEditShoppingList = async () => {
        if (selectedList) {
            const { error } = await supabase
                .from('Shoppinglist')
                .update({
                    Title: editTitle,
                    Description: editDescription,
                    Store: editStore,
                    Parts: editItems,
                    Amount: editAmount
                })
                .eq('id', selectedList.id);

            if (error) {
                console.error(error);
            } else {
                const { data } = await supabase
                    .from('Shoppinglist')
                    .select('*');
                setShoppingLists(data);
                handleCloseEditDialog();
            }
        }
    };

    const handleAddEditNewItem = () => {
        if (editNewItem.trim() !== "") {
            setEditItems([...editItems, editNewItem]);
            setEditAmount([...editAmount, newItemAmount || "0"]);
            setEditNewItem("");
            setNewItemAmount("");
        }
    };

    const handleRemoveEditItem = (index: number) => {
        setEditItems(editItems.filter((_, i) => i !== index));
        setEditAmount(editAmount.filter((_, i) => i !== index));
    };

    const handlePinnShoppingList = async () => {
        try {
            const { error } = await supabase
                .from('Shoppinglist')
                .update({ is_pinned: true })
                .in('id', checked);

            if (error) {
                throw error;
            }

            const updatedShoppingLists = shoppingLists.map(shoppinglist =>
                checked.includes(shoppinglist.id) ? { ...shoppinglist, is_pinned: true } : shoppinglist
            );

            setShoppingLists(updatedShoppingLists);
            setChecked([]);
        } catch (error) {
            console.error('Error pinning shopping lists:', error);
        }
    };

    const handleUnpinShoppingList = async () => {
        try {
            const { error } = await supabase
                .from('Shoppinglist')
                .update({ is_pinned: false })
                .in('id', checked);

            if (error) {
                throw error;
            }

            const updatedShoppingLists = shoppingLists.map(shoppinglist =>
                checked.includes(shoppinglist.id) ? { ...shoppinglist, is_pinned: false } : shoppinglist
            );

            setShoppingLists(updatedShoppingLists);
            setChecked([]);
        } catch (error) {
            console.error('Error unpinning shopping lists:', error);
        }
    };

    return (
        <div style={{width: 'auto', height: 'auto'}}>
            <div style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                zIndex: 1000,
            }}>
                {alert}
            </div>
            <Grid2 container spacing={2} style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                flexDirection: 'row'
            }}>
                <Grid2 style={{width:'fit-content'}}>
                    <List component="nav" sx={{width: '600px', bgcolor: 'background.paper'}}>
                        {shoppingLists.length === 0 ? (
                            <ListItem>
                                <ListItemText primary="There is no shopping list" />
                            </ListItem>
                        ) : (
                            shoppingLists.map((list) => {
                                const labelId = `checkbox-list-label-${list.id}`;

                                return (
                                    <ListItem
                                        key={list.id}
                                        secondaryAction={
                                            <IconButton edge="end" aria-label="delete">
                                                <ShoppingCartIcon />
                                            </IconButton>
                                        }
                                        disablePadding
                                    >
                                        <ListItemButton role={undefined} onClick={() => handleListItemClick(list)} dense>
                                            <ListItemIcon>
                                                <Checkbox
                                                    edge="start"
                                                    checked={checked.includes(list.id)}
                                                    tabIndex={-1}
                                                    disableRipple
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleToggle(list.id)();
                                                    }}
                                                />
                                            </ListItemIcon>
                                            <ListItemText id={labelId} primary={list.Title} secondary={list.Description} />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })
                        )}
                    </List>
                </Grid2>
                <Grid2 style={{width:'30%', height: 'auto', paddingLeft: '8px'}}>
                    <Button
                        variant="contained"
                        endIcon={<AddShoppingCartIcon/>}
                        onClick={handleNewShoppingList}
                        style={{width: '100%', height: '50px'}}
                    >
                        New Shopping List
                    </Button>
                    <Button
                        variant="contained"
                        endIcon={<RemoveShoppingCartIcon/>}
                        onClick={handleDeleteShoppingLists}
                        style={{width: '100%', height: '50px', marginTop: '10px'}}
                        disabled={checked.length === 0}
                    >
                        Delete Shopping List/s
                    </Button>
                    <Button
                        variant="contained"
                        endIcon={<ShoppingCartCheckoutIcon/>}
                        onClick={handleEditShoppingList}
                        style={{width: '100%', height: '50px', marginTop: '10px'}}
                        disabled={checked.length !== 1}
                    >
                        Edit Shopping List
                    </Button>
                    <Button
                        variant="contained"
                        endIcon={<PushPinIcon/>}
                        onClick={handlePinnShoppingList}
                        style={{width: '48%', height: '50px', marginTop: '10px', textSizeAdjust: 'auto'}}
                        disabled={checked.length === 0}
                    >
                        Pin Shopping List/s
                    </Button>
                    <Button
                        variant="contained"
                        endIcon={<UndoIcon/>}
                        onClick={handleUnpinShoppingList}
                        style={{width: '49%', height: '50px', marginTop: '10px', marginLeft:'10px', fontSize: '12px'}}
                        disabled={checked.length === 0}
                    >
                        Unpin Shopping List/s
                    </Button>
                </Grid2>
                <Dialog open={isNewDialogOpen} onClose={handleCloseNewDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>New Shopping List</DialogTitle>
                    <DialogContent>
                        <TextField
                            label="Title"
                            fullWidth
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            margin="normal"
                        />
                        <TextField
                            label="Description"
                            fullWidth
                            multiline
                            rows={4}
                            value={newDescription}
                            onChange={(e) => setNewDescription(e.target.value)}
                            margin="normal"
                        />
                        <TextField
                            label="Store"
                            fullWidth
                            value={newStore}
                            onChange={(e) => setNewStore(e.target.value)}
                            margin="normal"
                        />
                        <List sx={{ maxHeight: 200, overflow: 'auto', marginTop: 2, bgcolor: 'rgb(243, 229, 229)', borderRadius: 2 }}>
                            {newItems.map((item, index) => (
                                <React.Fragment key={index}>
                                    <ListItem
                                        secondaryAction={
                                            <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveNewItem(index)}>
                                                <DeleteForeverIcon />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText primary={`${newAmount[index]}x ${item}`} />
                                    </ListItem>
                                    {index < newItems.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                        <TextField
                            label="Amount"
                            type="number"
                            fullWidth
                            value={newItemAmount}
                            onChange={(e) => setNewItemAmount(String(e.target.value))}
                            margin="normal"
                            style={{width:'90px'}}
                        />
                        <TextField
                            label="New Item"
                            fullWidth
                            value={newItem}
                            onChange={(e) => setNewItem(e.target.value)}
                            margin="normal"
                            style={{width:'450px', marginLeft: '10px'}}
                        />
                        <Button variant="contained" startIcon={<AddShoppingCartIcon/>}  onClick={handleAddNewItem} color="primary" style={{marginTop: '10px'}}>
                            Add Item
                        </Button>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseNewDialog} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleSaveShoppingList} color="primary">
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog open={isViewDialogOpen} onClose={handleCloseViewDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>{selectedList ? selectedList.Title : ""}</DialogTitle>
                    <DialogContent>
                        {selectedList && (
                            <>
                                <Typography variant="body1" gutterBottom>
                                    {selectedList.Description}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Store: {selectedList.Store}
                                </Typography>
                                <List sx={{ maxHeight: 200, overflow: 'auto', marginTop: 2, bgcolor: 'rgb(243, 229, 229)', borderRadius: 2 }}>
                                    {selectedList.Parts.map((item, index) => (
                                        <React.Fragment key={index}>
                                            <ListItem>
                                                <ListItemText primary={`${selectedList.Amount[index]}x ${item}`} />
                                                <Checkbox
                                                    edge="end"
                                                    checked={selectedList.State[index]}
                                                    onChange={() => handleToggleItemState(index)}
                                                    tabIndex={-1}
                                                    disableRipple
                                                />
                                            </ListItem>
                                            {index < selectedList.Parts.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseViewDialog} color="primary">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog open={isEditDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>Edit Shopping List</DialogTitle>
                    <DialogContent>
                        <TextField
                            label="Title"
                            fullWidth
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            margin="normal"
                        />
                        <TextField
                            label="Description"
                            fullWidth
                            multiline
                            rows={4}
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            margin="normal"
                        />
                        <TextField
                            label="Store"
                            fullWidth
                            value={editStore}
                            onChange={(e) => setEditStore(e.target.value)}
                            margin="normal"
                        />
                        <List sx={{ maxHeight: 200, overflow: 'auto', marginTop: 2, bgcolor: 'rgb(243, 229, 229)', borderRadius: 2 }}>
                            {editItems.map((item, index) => (
                                <React.Fragment key={index}>
                                    <ListItem
                                        secondaryAction={
                                            <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveEditItem(index)}>
                                                <DeleteForeverIcon />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText primary={`${editAmount[index] || 0}x ${item}`} />
                                    </ListItem>
                                    {index < editItems.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                        <TextField
                            label="Amount"
                            type="number"
                            fullWidth
                            value={newItemAmount}
                            onChange={(e) => setNewItemAmount(String(e.target.value))}
                            margin="normal"
                            style={{width:'90px'}}
                        />
                        <TextField
                            label="New Item"
                            fullWidth
                            value={editNewItem}
                            onChange={(e) => setEditNewItem(e.target.value)}
                            margin="normal"
                            style={{width:'450px', marginLeft: '10px'}}
                        />
                        <Button variant="contained" startIcon={<AddShoppingCartIcon/>} onClick={handleAddEditNewItem} color="primary" style={{marginTop: '10px'}}>
                            Add Item
                        </Button>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseEditDialog} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleSaveEditShoppingList} color="primary">
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
            </Grid2>
        </div>
    );
}